import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TimePickerUTC, utcToLocal, useTimezone } from '@t-one-internal/design-system';
import type { Schedule, DayOfWeek, MonthlyOrdinal, MonthlyDayType, Recipient } from '../types';
import RecipientSelector from './RecipientSelector';

interface SchedulerProps {
    value: Schedule;
    onChange: (schedule: Schedule) => void;
}

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
];

const Scheduler: React.FC<SchedulerProps> = ({ value, onChange }) => {
    const { timezone } = useTimezone();

    const handleRecipientsChange = (recipients: Recipient[]) => {
        onChange({ ...value, recipients });
    };

    const handleFrequencyChange = (freq: Schedule['frequency']) => {
        // Set sensible defaults when switching frequencies
        const updates: Partial<Schedule> = { frequency: freq };
        if (freq === 'weekly' && !value.dayOfWeek) updates.dayOfWeek = 'monday';
        if (freq === 'monthly') {
            if (!value.monthlyOrdinal) updates.monthlyOrdinal = 'first';
            if (!value.monthlyDayType) updates.monthlyDayType = 'day';
        }
        onChange({ ...value, ...updates });
    };

    return (
        <div className="space-y-6">

            {/* Frequency */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Schedule Frequency</label>
                <div className="grid grid-cols-4 gap-2">
                    {(['once', 'daily', 'weekly', 'monthly'] as const).map((freq) => (
                        <button
                            key={freq}
                            type="button"
                            onClick={() => handleFrequencyChange(freq)}
                            className={`py-2 text-sm font-medium rounded-lg border transition-all ${value.frequency === freq
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700 ring-1 ring-indigo-500'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Dynamic Schedule Details */}
            {value.frequency !== 'once' && (
                <div className="p-4 bg-white rounded-lg border border-gray-100 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="flex flex-col sm:flex-row gap-4">

                        {/* Weekly: Day of Week Picker */}
                        {value.frequency === 'weekly' && (
                            <div className="flex-1 space-y-1">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Day of Week</label>
                                <Select
                                    value={value.dayOfWeek || 'monday'}
                                    onValueChange={(val: string) => onChange({ ...value, dayOfWeek: val as DayOfWeek })}
                                >
                                    <SelectTrigger className="w-full bg-white">
                                        <SelectValue placeholder="Select day" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {DAYS_OF_WEEK.map((day) => (
                                            <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Monthly: 3 Dropdowns (Ordinal + DayType + Time) */}
                        {value.frequency === 'monthly' && (
                            <>
                                <div className="flex-1 space-y-1">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Occurrence</label>
                                    <Select
                                        value={value.monthlyOrdinal || 'first'}
                                        onValueChange={(val: string) => onChange({ ...value, monthlyOrdinal: val as MonthlyOrdinal })}
                                    >
                                        <SelectTrigger className="w-full bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="first">First</SelectItem>
                                            <SelectItem value="last">Last</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex-1 space-y-1">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Day</label>
                                    <Select
                                        value={value.monthlyDayType || 'day'}
                                        onValueChange={(val: string) => onChange({ ...value, monthlyDayType: val as MonthlyDayType })}
                                    >
                                        <SelectTrigger className="w-full bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="day">Day</SelectItem>
                                            <SelectItem value="weekday">Weekday</SelectItem>
                                            {DAYS_OF_WEEK.map((day) => (
                                                <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}

                        {/* Time Picker (Always show for recurring) */}
                        <div className="flex-1">
                            <TimePickerUTC
                                label="Time"
                                value={value.time}
                                onChange={(utcTime) => onChange({ ...value, time: utcTime })}
                                displayTimezone={timezone}
                                showTimezoneBadge={true}
                            />
                        </div>
                    </div>

                    {/* Helper Text */}
                    {value.time && (
                        <p className="mt-3 text-xs text-gray-500 flex items-start gap-1">
                            <span className="mt-0.5 w-1 h-1 rounded-full bg-indigo-500 shrink-0 block" />
                            <span>
                                {(() => {
                                    const localTime = utcToLocal(value.time, timezone);
                                    return (
                                        <>
                                            {value.frequency === 'daily' && `Report will be sent every day at ${localTime}.`}
                                            {value.frequency === 'weekly' && `Report will be sent every ${value.dayOfWeek} at ${localTime}.`}
                                            {value.frequency === 'monthly' && (
                                                <>
                                                    Report will be sent on the <strong className="font-semibold text-gray-700">{value.monthlyOrdinal} {value.monthlyDayType}</strong> of every month at {localTime}.
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </span>
                        </p>
                    )}
                </div>
            )}

            {/* Recipients */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Recipients</label>
                <RecipientSelector
                    value={value.recipients}
                    onChange={handleRecipientsChange}
                />
            </div>

        </div>
    );
};

export default Scheduler;
